import { showNotification } from '@mantine/notifications';
import { IconButton } from '@repo/foundations';
import { useCallback, useState } from 'react';
import type { AiAttachment } from '../../../api/hooks/ai/useAIConversation';
import { uploadFile } from '../../RichEditor/RichEditor.utils';

export function AttachmentButton({
	attachments,
	onAttachmentUpload,
}: {
	attachments: AiAttachment[];
	onAttachmentUpload: (attachment: AiAttachment) => void;
}) {
	const [isLoading, setIsLoading] = useState(false);

	const handleAttachmentSelection = useCallback(() => {
		if (attachments.length >= 10) {
			showNotification({
				title: 'Upload limit reached',
				message: 'You can only upload up to 10 files at a time',
				color: 'red',
			});
			return;
		}

		const input = document.createElement('input');
		input.type = 'file';
		input.multiple = true;
		input.onchange = async (e: Event) => {
			setIsLoading(true);

			try {
				const { files } = e.target as HTMLInputElement;
				if (files?.length) {
					await Promise.all(
						[...files].map(async (file) => {
							const result = await uploadFile(file, false, true);
							onAttachmentUpload({ url: result, filename: file.name });
						})
					);
				}
			} finally {
				setIsLoading(false);
			}
		};
		input.click();
	}, [attachments.length, onAttachmentUpload]);

	return (
		<IconButton
			iconName="paperclip"
			variant="tertiary"
			onClick={handleAttachmentSelection}
			loading={isLoading}
			disabled={isLoading}
			tooltip="Attach files"
			tooltipProps={{
				offset: 8,
			}}
			data-testid="attach-files-button"
		/>
	);
}
